import React, { Component } from 'react'
import { connect } from 'react-redux'
import DocumentEvents from 'react-document-events'

import Dropzone from 'react-dropzone'
import { getAppId } from 'utils/location'
import { uploadCustomIcon } from 'utils/io'
import { getCustomIcons, fetchCustomIcons } from 'ducks/apps'
import { assetURL } from 'utils/assets'
import { getFeatureFlag } from 'ducks/featureFlags'
import { getIconsList } from '../../../utils/material'
import { ESC, RETURN } from '../../../utils/keyboard'
import { filterByQuery } from '../../../utils/search'
import Icon, { IconButton } from '../Icon'
import Loading from '../Loading'
import './IconMenu.css'
import './MaterialIcons.css'

const getIconName = value => {
  value = String(value || '')

  return value.replace(/\-/g, '_')
}

class IconMenu extends Component {
  state = {
    isOpen: false,
    searchText: '',
    uploadingFile: false,
  }

  errorMessage = error => {
    console.error(error)
  }

  handleOpen = () => {
    this.setState({ isOpen: true })
  }

  handleClose = () => {
    this.setState({ isOpen: false, searchText: '' })
  }

  handleSearch = e => {
    this.setState({ searchText: e.currentTarget.value.trim() })
  }

  handleKeyDown = e => {
    if (e.which === ESC || e.which === RETURN) {
      this.handleClose()
    }
  }

  handleChange = val => {
    let { onChange, input } = this.props

    if (input && input.onChange) {
      onChange = input.onChange
    }

    onChange(val)

    this.handleClose()
  }

  handleClear = e => {
    e.preventDefault()
    e.stopPropagation()

    this.handleChange(null)
  }

  handleDrop = acceptedFiles => {
    const file = acceptedFiles[0]
    const { fetchCustomIcons } = this.props

    if (!file) {
      return this.errorMessage('No valid file')
    }

    const filename = file.name

    const reader = new FileReader()

    reader.onload = async event => {
      this.setState({ uploadingFile: reader.result })

      try {
        const appId = getAppId(window.location)
        await uploadCustomIcon(appId, file, filename)
        fetchCustomIcons(appId)
      } catch (err) {
        console.error('ERROR UPLOADING IMAGE:', err)
        this.errorMessage()
      }

      this.setState({ uploadingFile: null })
    }

    if (file) {
      reader.readAsDataURL(file)
    }
  }

  renderValue() {
    const { value } = this.props

    if (!value) {
      return <div className="material-icon-menu-placeholder">None</div>
    }

    if (typeof value === 'object') {
      const { url, filename } = value.body
      const title = filename.split('.')[0]
      const fullURL = assetURL(url)

      return (
        <div className="material-icon-menu-title">
          <img src={fullURL} alt={title} style={{ width: 24, height: 24 }} />{' '}
          {title}
        </div>
      )
    }

    const name = getIconName(value)

    return (
      <div className="material-icon-menu-title">
        <i className="material-icons">{name}</i> {name}
      </div>
    )
  }

  renderMenu() {
    let options = [...getIconsList()]
    const { searchText, uploadingFile } = this.state
    // showCustomIcons is whether or not a component supports custom icons
    // hasCustomIcons is the feature flag
    // customIcons is the list of custom icons
    const { showCustomIcons = true, customIcons, hasCustomIcons } = this.props
    const useCustomIcons = hasCustomIcons && showCustomIcons

    if (searchText) {
      options = filterByQuery(options, searchText)
    }

    if (useCustomIcons && customIcons) {
      const customIconsArray = []
      for (const icon of Object.values(customIcons)) {
        if (searchText) {
          const query = searchText.trim()
          const terms = query.split(/\s+/)
          let addIcon = false

          for (const term of terms) {
            if (icon.body.filename.includes(term)) {
              addIcon = true

              break
            }
          }

          if (addIcon) {
            const { id, body } = icon
            customIconsArray.push({ id, body, type: 'customIcon' })
          }
        } else {
          const { id, body } = icon
          customIconsArray.push({ id, body, type: 'customIcon' })
        }
      }

      options = customIconsArray.concat(options)
    }

    return (
      <React.Fragment>
        <div
          className="material-icon-menu-backdrop"
          onMouseDown={this.handleClose}
        />
        <div className="material-icon-menu-options">
          <div className="material-icon-menu-search">
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={this.handleSearch}
            />
            {useCustomIcons && (
              <Dropzone
                className="material-icon-menu-search-button"
                accept={['image/svg+xml']}
                onDrop={this.handleDrop}
              >
                {({ getRootProps, getInputProps }) => (
                  <div
                    className="material-icon-menu-search-button"
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />
                    <p>
                      {uploadingFile ? (
                        <Loading small expanded color="white" />
                      ) : (
                        '+'
                      )}
                    </p>
                  </div>
                )}
              </Dropzone>
            )}
          </div>
          <div className="material-icon-menu-options-sub">
            {options.map(opt => (
              <MenuOption
                key={
                  opt?.body?.filename
                    ? `custom-${opt?.body?.filename}`
                    : `${opt}`
                }
                value={opt}
                onSelect={this.handleChange}
              />
            ))}
          </div>
        </div>
      </React.Fragment>
    )
  }

  render() {
    const { isOpen } = this.state
    let { value, input } = this.props

    // Redux-form compatibility
    if (input) {
      value = input.value
    }

    return (
      <div className="material-icon-menu">
        <DocumentEvents onKeyDown={this.handleKeyDown} />
        <div className="material-icon-menu-value" onClick={this.handleOpen}>
          {this.renderValue()}
          {value ? (
            <IconButton
              type="close"
              onClick={this.handleClear}
              className="material-icon-menu-clear"
            />
          ) : null}
          <Icon type="expand-vertical" className="material-icon-menu-expand" />
        </div>
        {isOpen ? this.renderMenu() : null}
      </div>
    )
  }
}

class MenuOption extends Component {
  handleClick = () => {
    const { onSelect, value } = this.props

    onSelect(value)
  }

  render() {
    const { value } = this.props

    if (typeof value === 'object') {
      const { url, filename } = value.body
      const title = filename.split('.')[0]
      const fullURL = assetURL(url)

      return (
        <div
          className="material-icon-menu-option"
          onClick={this.handleClick}
          title={title}
        >
          <img src={fullURL} alt={title} style={{ width: 24, height: 24 }} />
        </div>
      )
    }

    const iconName = getIconName(value)

    return (
      <div
        className="material-icon-menu-option"
        onClick={this.handleClick}
        title={value}
      >
        <i className="material-icons">{iconName}</i>
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    customIcons: getCustomIcons(state, getAppId(window.location)),
    hasCustomIcons: getFeatureFlag(state, 'hasCustomIcons'),
  }
}

export default connect(mapStateToProps, { fetchCustomIcons })(IconMenu)
